export type UserType = 'admin' | 'agent' | 'engineer' | 'client'

export const USER_TYPE_NAMES: Record<UserType, string> = {
  'admin': 'Administrator',
  'agent': 'Agent',
  'engineer': 'Engineer',
  'client': 'Client'
} as const

export const AVAILABLE_USER_TYPES: UserType[] = Object.keys(USER_TYPE_NAMES) as UserType[]

// Type guard
export function isValidUserType(type: string | null): type is UserType {
  return type !== null && type in USER_TYPE_NAMES
}

// User type-based permissions
export const USER_TYPE_PERMISSIONS: Record<UserType, string[]> = {
  'admin': [
    '*', // Admin has all permissions
    'user.manage',
    'system.config',
    'audit.view',
    'report.generate'
  ],
  'agent': [
    'dashboard.view',
    'tender.create',
    'tender.submit',
    'tender.track',
    'tender.view',
    'client.manage',
    'document.upload',
    'communication.send'
  ],
  'engineer': [
    'dashboard.view',
    'tender.review',
    'tender.assign',
    'tender.update',
    'technical.review',
    'specification.verify',
    'feedback.submit',
    'report.view'
  ],
  'client': [
    'dashboard.view',
    'tender.view',
    'tender.bid',
    'feedback.submit',
    'profile.view',
    'document.view',
    'communication.view'
  ]
}

// Simple user type management functions
export function setUserType(userType: UserType): void {
  if (typeof window !== 'undefined') {
    if (!isValidUserType(userType)) {
      console.warn(`Invalid user type: ${userType}. Defaulting to 'agent'.`)
      userType = 'agent'
    }

    localStorage.setItem('user_type', userType)
    console.log(`User type set to: ${USER_TYPE_NAMES[userType]}`)
  }
}

export function getCurrentUserType(): UserType {
  if (typeof window !== 'undefined') {
    const storedType = localStorage.getItem('user_type') as UserType
    return isValidUserType(storedType) ? storedType : 'agent'
  }
  return 'agent'
}

// Get user type from auth store or localStorage
export function getUserType(): UserType {
  if (typeof window !== 'undefined') {
    try {
      // Try to get from auth store first - check both 'auth' and 'user_data'
      const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth')
      if (authData) {
        const parsed = JSON.parse(authData)
        // Handle different possible structures
        const userType = parsed.user?.userType || parsed.userType
        if (userType && isValidUserType(userType)) {
          return userType
        }
      }

      // Try legacy user_data format
      const userData = localStorage.getItem('user_data')
      if (userData) {
        const parsed = JSON.parse(userData)
        const userType = parsed.userType || parsed.user?.userType
        if (userType && isValidUserType(userType)) {
          return userType
        }
      }
    } catch (error) {
      console.warn('Failed to parse user data:', error)
    }

    // Fallback to localStorage user type
    return getCurrentUserType()
  }
  return 'agent'
}

export function getUserTypeName(userType?: UserType): string {
  const targetType = userType || getUserType()
  return USER_TYPE_NAMES[targetType] || 'Agent'
}

export function hasPermission(permission: string, userType?: UserType): boolean {
  const targetType = userType || getUserType()

  // Admin has all permissions
  if (targetType === 'admin') {
    return true
  }

  const permissions = USER_TYPE_PERMISSIONS[targetType] || []
  return permissions.includes('*') || permissions.includes(permission)
}

// Helper functions to check user type
export function isAdmin(userType?: UserType): boolean {
  const targetType = userType || getUserType()
  return targetType === 'admin'
}

export function isAgent(userType?: UserType): boolean {
  const targetType = userType || getUserType()
  return targetType === 'agent'
}

export function isEngineer(userType?: UserType): boolean {
  const targetType = userType || getUserType()
  return targetType === 'engineer'
}

export function isClient(userType?: UserType): boolean {
  const targetType = userType || getUserType()
  return targetType === 'client'
}

// User type-based route guards
export function getDashboardRoute(): string {
  return '/dashboard'
}

export function getDefaultRoute(userType?: UserType): string {
  const targetType = userType || getUserType()
  switch (targetType) {
    case 'admin':
      return '/admin/dashboard'
    case 'agent':
      return '/agent/dashboard'
    case 'engineer':
      return '/engineer/dashboard'
    case 'client':
      return '/client/dashboard'
    default:
      return '/dashboard'
  }
}

// Sync user type from auth store
export function syncUserTypeFromAuthStore(): void {
  if (typeof window !== 'undefined') {
    try {
      // Check both storages and both keys
      const authData = localStorage.getItem('auth') || sessionStorage.getItem('auth')
      if (authData) {
        const parsed = JSON.parse(authData)
        const userType = parsed.user?.userType || parsed.userType
        if (userType && isValidUserType(userType)) {
          setUserType(userType)
          console.log('✅ User type synced from auth store:', userType)
          return
        }
      }

      // Try legacy format
      const userData = localStorage.getItem('user_data')
      if (userData) {
        const parsed = JSON.parse(userData)
        const userType = parsed.userType || parsed.user?.userType
        if (userType && isValidUserType(userType)) {
          setUserType(userType)
          console.log('✅ User type synced from legacy data:', userType)
        }
      }
    } catch (error) {
      console.warn('Failed to sync user type:', error)
    }
  }
}

// Clear user type from storage
export function clearUserType(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('user_type')
  }
}

// Initialize user type on load - THIS WAS MISSING
export function initializeUserType(): void {
  if (typeof window !== 'undefined') {
    syncUserTypeFromAuthStore()
  }
}

// Check if user can access a route
export function canAccessRoute(routeName: string, userType?: UserType): boolean {
  const targetType = userType || getUserType()

  // Admin can access everything
  if (targetType === 'admin') {
    return true
  }

  const routePermissions: Record<UserType, string[]> = {
    'admin': ['*'],
    'agent': [
      'dashboard',
      'tenders',
      'clients',
      'documents',
      'communications',
      'reports'
    ],
    'engineer': [
      'dashboard',
      'tenders',
      'reviews',
      'technical',
      'reports'
    ],
    'client': [
      'dashboard',
      'tenders',
      'bids',
      'profile',
      'documents'
    ]
  }

  const allowedRoutes = routePermissions[targetType] || []
  return allowedRoutes.includes('*') ||
         allowedRoutes.some(allowed => routeName.includes(allowed))
}

// Export all as default object (for backward compatibility)
export default {
  USER_TYPE_NAMES,
  AVAILABLE_USER_TYPES,
  isValidUserType,
  setUserType,
  getCurrentUserType,
  getUserType,
  getUserTypeName,
  hasPermission,
  isAdmin,
  isAgent,
  isEngineer,
  isClient,
  getDashboardRoute,
  getDefaultRoute,
  canAccessRoute,
  syncUserTypeFromAuthStore,
  clearUserType,
  initializeUserType
}
